// Sprint 9.4 + 10.3 + 10.x — Activity tab : KPIs (Plans PostHog + Sub) +
// Sparkline + events PostHog réels.
// Source : posthog_pulse (mode contact via email) + posthog_contact_activity.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useGetList, useRecordContext } from "ra-core";
import { Database, FileText, LogIn, TrendingUp } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";

import { Card, CardContent } from "@/components/ui/card";
import { Sparkline } from "../shared/Sparkline";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { ContactActivityReport } from "./ContactActivityReport";
import { ContactProductSignals } from "./ContactProductSignals";
import type { ContactSignalsResponse } from "./contactSignals";
import type { Contact, Subscription } from "../types";

type PulseKpiResponse = {
  configured: boolean;
  kpis: { key: string; value: number }[];
};

function usePostHogPulse(email: string | null, days: number) {
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    if (!email) {
      setCounts(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await getSupabaseClient().functions.invoke(
        "posthog_pulse",
        { body: { email, days } },
      );
      if (cancelled || !data) return;
      const resp = data as PulseKpiResponse;
      if (!resp.configured) {
        setCounts(null);
        return;
      }
      const map: Record<string, number> = {};
      for (const k of resp.kpis) map[k.key] = k.value;
      setCounts(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [email, days]);
  return counts;
}

function useContactSignals(email: string | null) {
  return useQuery({
    queryKey: ["contact_product_signals", email],
    enabled: !!email,
    staleTime: 60_000,
    queryFn: async (): Promise<ContactSignalsResponse | null> => {
      const { data, error } =
        await getSupabaseClient().functions.invoke<ContactSignalsResponse>(
          "posthog_contact_signals",
          { body: { email, days: 90 } },
        );
      if (error) throw error;
      return data ?? null;
    },
  });
}

function KPICard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-fabrik-card border border-border bg-card px-4 py-3 shadow-card">
      <div className="flex items-center gap-2 text-fabrik-text-3 mb-1">
        <span className="[&>svg]:size-3.5">{icon}</span>
        <p className="text-[10.5px] uppercase tracking-wider font-semibold">
          {label}
        </p>
      </div>
      <p className="text-[18px] font-bold text-foreground tabular-nums leading-tight">
        {value}
      </p>
      {hint && <p className="text-[11px] text-fabrik-text-3 mt-0.5">{hint}</p>}
    </div>
  );
}

function getPrimaryEmail(c: Contact): string | null {
  const arr = c.email_jsonb as Array<{ email: string }> | undefined;
  return arr?.[0]?.email ?? null;
}

const SUB_LABEL: Record<string, string> = {
  active: "Actif",
  trialing: "Discovery (essai)",
  canceled: "Ancien payant",
  past_due: "En retard",
  incomplete: "Incomplet",
};

export function ContactActivityTab() {
  const record = useRecordContext<Contact>();
  const email = record ? getPrimaryEmail(record) : null;
  const pulse30 = usePostHogPulse(email, 30);
  const pulse365 = usePostHogPulse(email, 365);
  const { data: signals, isPending: signalsLoading } = useContactSignals(email);
  const { data: subs } = useGetList<Subscription>(
    "subscriptions",
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "started_at", order: "DESC" },
      filter: { company_id: record?.company_id },
    },
    { enabled: !!record?.company_id },
  );
  if (!record) return null;

  if (record.population !== "plateforme") {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="size-11 mx-auto mb-3 rounded-xl bg-muted text-muted-foreground flex items-center justify-center">
            <Database className="size-5" />
          </div>
          <p className="text-[14px] font-medium text-foreground mb-1">
            Activité plateforme non applicable
          </p>
          <p className="text-[12.5px] text-muted-foreground max-w-md mx-auto">
            Ce contact est marqué comme <strong>Prospection</strong>, pas comme
            user fabrik.so. L'onglet Activité affichera son usage produit dès
            qu'il sera classé Plateforme et que la synchronisation PostHog sera
            active.
          </p>
        </CardContent>
      </Card>
    );
  }

  const activeSub = subs?.find(
    (s) => s.status === "active" || s.status === "trialing",
  );
  // Cas "Ancien payant" : subs existent mais aucune active/trialing.
  const formerPayer =
    !activeSub && (subs?.some((s) => s.status === "canceled") ?? false);
  const subLabel = activeSub
    ? (activeSub.product_name ??
      SUB_LABEL[activeSub.status] ??
      activeSub.status)
    : formerPayer
      ? "Ancien payant"
      : "—";
  const plans30 = pulse30?.plan_created ?? 0;
  const plansTotal = pulse365?.plan_created ?? 0;
  const lastSeen = record.last_seen;
  const lastSeenRel = lastSeen
    ? formatDistanceToNowStrict(new Date(lastSeen), {
        locale: fr,
        addSuffix: true,
      })
    : "—";

  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard
          icon={<FileText />}
          label="Plans (30j)"
          value={plans30}
          hint={pulse30 === null ? "PostHog non configuré" : undefined}
        />
        <KPICard
          icon={<FileText />}
          label="Plans (total)"
          value={plansTotal}
          hint={
            plansTotal === 0 && pulse365 !== null
              ? "Aucun plan pour l'instant"
              : undefined
          }
        />
        <KPICard icon={<LogIn />} label="Dernier login" value={lastSeenRel} />
        <KPICard
          icon={<TrendingUp />}
          label="Abonnement"
          value={subLabel}
          hint={
            activeSub?.status === "trialing"
              ? "Période d'essai"
              : formerPayer
                ? "Churné"
                : undefined
          }
        />
      </div>

      {record.last_activity_sparkline &&
        record.last_activity_sparkline.length > 0 && (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[13px] font-semibold text-foreground">
                  Activité (30j)
                </p>
                <span className="text-[11px] text-fabrik-text-3">
                  {record.last_activity_sparkline.reduce((s, v) => s + v, 0)}{" "}
                  évts
                </span>
              </div>
              <Sparkline
                data={record.last_activity_sparkline}
                width={400}
                height={48}
              />
            </CardContent>
          </Card>
        )}

      <ContactProductSignals
        signals={signals ?? null}
        loading={signalsLoading && !!email}
      />

      <ContactActivityReport />
    </div>
  );
}
